import { useState, useMemo, useEffect } from 'react';
import Papa from 'papaparse';
import { Search, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Textarea } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Dropzone } from '@/components/ui/Dropzone';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { readFileAsText } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

const SAMPLE = `name,role,city,salary
Aarav,Engineer,Mumbai,1800000
Priya,Designer,Bengaluru,1500000
Rohan,PM,Pune,2200000
Diya,Analyst,Delhi,1300000
Vivaan,Operator,Hyderabad,900000
Anaya,Founder,Goa,3500000`;

export function CsvViewer() {
  const { pendingFile, setPendingFile } = useApp();
  const [raw, setRaw] = useState(SAMPLE);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (pendingFile && (pendingFile.type === 'text/csv' || pendingFile.name.endsWith('.csv'))) {
      readFileAsText(pendingFile).then(setRaw);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsed = useMemo(() => {
    if (!raw.trim()) return { headers: [] as string[], rows: [] as Record<string, string>[] };
    const r = Papa.parse<Record<string, string>>(raw, {
      header: true,
      skipEmptyLines: true,
    });
    return {
      headers: (r.meta.fields ?? []) as string[],
      rows: r.data,
    };
  }, [raw]);

  const filtered = useMemo(() => {
    let rows = parsed.rows;
    const q = filter.toLowerCase().trim();
    if (q) {
      rows = rows.filter((r) =>
        Object.values(r).some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    if (sortKey) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        const numeric = !Number.isNaN(an) && !Number.isNaN(bn);
        const cmp = numeric ? an - bn : av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [parsed, filter, sortKey, sortDir]);

  function exportCsv() {
    const csv = Papa.unparse(filtered);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'view.csv');
    toast.success('Saved');
  }

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="csv-viewer"
        title="CSV viewer"
        description="Load any CSV — search across all columns, click headers to sort. Exports the current view."
        badges={
          <Badge>{filtered.length} of {parsed.rows.length} rows</Badge>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
            <Input
              icon={<Search size={12} />}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter rows…"
              className="!h-8 max-w-xs"
            />
            <Button size="sm" variant="ghost" onClick={exportCsv}>
              <Download size={11} />
              Export
            </Button>
          </div>
          <div className="overflow-auto max-h-[520px]">
            <table className="w-full text-sm">
              <thead className="bg-bg-inset sticky top-0 z-10">
                <tr>
                  {parsed.headers.map((h) => (
                    <th
                      key={h}
                      onClick={() => toggleSort(h)}
                      className={cn(
                        'text-left px-3 py-2 text-xs font-mono font-semibold border-b border-border cursor-pointer select-none whitespace-nowrap',
                        sortKey === h ? 'text-accent' : 'text-fg-muted hover:text-fg',
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {h}
                        {sortKey === h ? (
                          sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
                        ) : (
                          <ArrowUpDown size={10} className="opacity-30" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-surface-hover transition-colors">
                    {parsed.headers.map((h) => (
                      <td key={h} className="px-3 py-2 border-b border-border whitespace-nowrap">
                        {r[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            CSV source
          </div>
          <Textarea
            mono
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[400px] !bg-transparent focus-visible:!ring-0 text-[12px]"
          />
        </Card>
      </div>
    </div>
  );
}

export default CsvViewer;
